import { observer } from "mobx-react";
import { AppBar } from "@/components-v2/AppBar";
import { FollowUpLeftPanel } from "./followUpComponents";
import { isFailure, isOk, isPending } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Box, Card, Container, VBox } from "@/components-v2/Box";
import { Icon } from "@/components-v2/Icon";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import {
  extractPortfolioFollowUpShareholdersUrl,
  PortfolioType,
  SelectedPortfolioOption,
  usePortfolio,
} from "@/portfolio/portfolioDomain";
import { useAppBar } from "@/appBar/useAppBar";
import { useEffect, useState } from "react";
import {
  filterShareholderFollowUps,
  FollowUpShareholdersStore,
  followUpShareholderTabs,
  followUpShareholderTabToLabel,
  GetShareholdersFollowUpResponse,
  mapToFollowUpShareholderTab,
  ShareholderFollowUp,
  ShareholderFollowUpTab,
  ShareholderUpdatedNameFollowUp,
} from "./shareholdersFollowUpDomain";
import { adminShareholders } from "@/utils/navigation";
import { navigate } from "@/events/eventsDomain";
import { ShareholderFollowUpReviewDialog } from "./ShareholderFollowUpReviewDialog";
import { Tabs } from "@/components-v2/Tabs";
import { MTextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";

export const FollowUpShareholdersPage = observer(function FollowUpShareholdersPage({
  portfolioType,
  portfolioId,
  contextCompany,
  tab,
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  contextCompany?: string;
  tab?: string;
}) {
  const rootStore = useRootStore();
  const { userStore, messageStore, http } = useRootStore();
  const { t } = useTranslation();
  const { portfolio } = usePortfolio({
    activeHeader: "follow-up",
    portfolioId,
    portfolioType,
  });
  const isCompanyContextPage = contextCompany !== undefined;
  const appBar = useAppBar({
    portfolioHeader: "follow-up",
    isPortfolioPage: !isCompanyContextPage,
    selectedPortfolioOption: portfolio,
  });
  const [store] = useState(
    () =>
      new FollowUpShareholdersStore(
        http,
        messageStore,
        userStore,
        rootStore,
        portfolio,
        contextCompany,
      ),
  );
  useEffect(() => {
    store.fetchFollowUps();
  }, [store, portfolio, contextCompany]);

  const activeTab = mapToFollowUpShareholderTab(tab);
  return (
    <FullScreenLayout
      alertStore={messageStore}
      appBar={<AppBar data={appBar} disableDropShadow />}
      centerPanelContainerProps={{ htmlProps: { style: { flex: "1 1 auto" } } }}
      leftPanelContainerProps={{ htmlProps: { style: { flex: "0 0 300px" } } }}
      leftPanel={
        <FollowUpLeftPanel
          portfolio={portfolio}
          selectedTab={"shareholders"}
          showAdminLinks={userStore.hasAnyAdminRole}
          contextCompany={contextCompany}
        />
      }
      centerPanel={
        isOk(store.fetchResponse) ? (
          <>
            {store.fetchResponse.data.followUp.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <Header
                  data={store.fetchResponse.data}
                  portfolio={portfolio}
                  activeTab={activeTab}
                  contextCompany={contextCompany}
                />
                <FollowUpShareholdersView
                  data={store.fetchResponse.data.followUp}
                  store={store}
                  activeTab={activeTab}
                />
              </>
            )}
          </>
        ) : isPending(store.fetchResponse) ? (
          <LoadingIndicator fullscreen />
        ) : isFailure(store.fetchResponse) ? (
          <VBox alignItems={"center"} justifyContent={"center"} fullheight>
            <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
            <Box>{t("error.fetch.data")}</Box>
            <LinkButton label={{ key: "try-again" }} onClick={() => store.fetchFollowUps()} />
          </VBox>
        ) : undefined
      }
      disableAppBarShadow
    />
  );
});

const Header = observer(function Header({
  activeTab,
  data,
  portfolio,
  contextCompany,
}: {
  activeTab?: ShareholderFollowUpTab;
  data: GetShareholdersFollowUpResponse;
  portfolio: SelectedPortfolioOption;
  contextCompany?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tabs = followUpShareholderTabs.map((filter) => ({
    id: filter,
    label: {
      key: followUpShareholderTabToLabel[filter],
      params: { count: filterShareholderFollowUps(data.followUp, filter).length },
    },
    onSelect: () =>
      dispatch([
        navigate(extractPortfolioFollowUpShareholdersUrl(portfolio, filter, contextCompany)),
      ]),
    isActive: activeTab === filter,
  }));

  return (
    <Box bg>
      <VBox maxWidth="xl" container marginTop="lg">
        <h4>{t("followup.shareholders")}</h4>
        <Tabs tabs={tabs} scrollable={false} />
      </VBox>
    </Box>
  );
});

const FollowUpShareholdersView = observer(function FollowUpShareholdersView({
  data,
  store,
  activeTab,
}: {
  data: ShareholderFollowUp[];
  store: FollowUpShareholdersStore;
  activeTab: ShareholderFollowUpTab;
}) {
  const tabData = filterShareholderFollowUps(data, activeTab);
  if (activeTab === "followup" && tabData.length === 0) {
    return <EmptyState />;
  }
  const showCompanyFilter = store
    .filterData(tabData)
    .some(
      (value) =>
        (value.type === "updated-name" && value.shareholders.length >= 1) ||
        value.type !== "missing-email",
    );
  return (
    <Container padding maxWidth="xl" gap={"md"}>
      <Box>
        <Box>
          <MTextInput
            model={store.nameFilter}
            type={"search"}
            icon={"magnifying-glass"}
            placeholder={{ key: "search" }}
          />
          {showCompanyFilter && <MSelect model={store.companyFilter} />}
        </Box>
      </Box>
      {store.filterData(tabData).map((value) => {
        switch (value.type) {
          case "updated-name":
            return <FollowUpShareholdersCard key={value.orgNo} data={value} store={store} />;
          case "missing-email":
            return <MissingEmailCard key={"missing-email"} store={store} />;
          default:
            return null;
        }
      })}
      {store.showReviewDialogData && (
        <ShareholderFollowUpReviewDialog
          model={store.showReviewDialogData.model}
          onCancel={store.closeReviewDialog}
          onSubmit={store.submitReviewDialog}
          isSubmitting={store.isSubmitting}
        />
      )}
    </Container>
  );
});

const EmptyState = observer(function EmptyState() {
  const { t } = useTranslation();
  return (
    <VBox fullheight alignItems={"center"} justifyContent={"center"}>
      <VBox alignItems={"center"} maxWidth={"md"}>
        <Icon name={"notebook"} size={"jumbo"} duotone />
        <h3>{t("followup.shareholders.empty-state.header")}</h3>
        <Box textAlign={"center"}>{t("followup.shareholders.empty-state.text")}</Box>
      </VBox>
    </VBox>
  );
});

const FollowUpShareholdersCard = observer(function FollowUpShareholdersCard({
  data,
  store,
}: {
  data: ShareholderUpdatedNameFollowUp;
  store: FollowUpShareholdersStore;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  return (
    <Card
      padding={"lg"}
      gap={"md"}
      title={
        <LinkButton
          weight={"normal"}
          label={{ key: "followup.shareholders.link", params: { companyName: data.companyName } }}
          onClick={() => dispatch([navigate(adminShareholders(data.orgNo))])}
        />
      }
      action={
        !data.isDismissed ? (
          <IconButton
            icon={"x"}
            tooltip={{ key: "remove" }}
            onClick={() => store.dismissUpdates(data.orgNo)}
          />
        ) : undefined
      }
    >
      <h4>{t("followup.shareholders.heading")}</h4>
      <p>{t("followup.shareholders.text", { count: data.shareholders.length })}</p>
      <Box>
        <Button
          variant={"secondary"}
          label={{ key: "followup.shareholders.review-button" }}
          onClick={() => store.openReviewDialog(data)}
        />
      </Box>
    </Card>
  );
});

const MissingEmailCard = observer(({ store }: { store: FollowUpShareholdersStore }) => {
  const { t } = useTranslation();
  return (
    <Card
      title={
        <LinkButton
          weight="normal"
          label={{ key: "followup.contacts.shareholder" }}
          onClick={store.navigateToPortfolioContacts}
        />
      }
    >
      <h4>{t("followup.shareholders.emailMissing.heading")}</h4>
      <p>{t("followup.shareholders.emailMissing.text")}</p>
      <Box>
        <Button
          variant="secondary"
          label={{ key: "followup.shareholders.review-button" }}
          onClick={store.reviewContactsByMissingEmail}
        />
      </Box>
    </Card>
  );
});
